<template>
  <div class="page-wrapper">
    <!-- Título -->
    <h2>My Products</h2>

    <!-- Barra de pesquisa -->
    <div class="search-bar">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="Search products..."
      />
    </div>

    <!-- Botão para adicionar produtos (abaixo da barra de pesquisa) -->
    <div class="add-button-container">
      <button @click="toggleAddForm">
        {{ showAddForm ? 'Close Form' : 'Add Product' }}
      </button>
    </div>

    <!-- Formulário para Adicionar Produto (dentro de um card) -->
    <div v-if="showAddForm" class="card form-card">
      <div class="card-header">
        <h3>Add New Product</h3>
      </div>
      <div class="card-body">
        <div class="form-group">
          <label>SKU</label>
          <input
            v-model="newProduct.sku"
            type="text"
            placeholder="Enter SKU"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label>Product Name</label>
          <input
            v-model="newProduct.name"
            type="text"
            placeholder="Enter Product Name"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label>Image URL</label>
          <input
            v-model="newProduct.img"
            type="text"
            placeholder="Enter Image URL"
            class="form-control"
          />
        </div>
        <div class="btn-group">
          <button @click="addNewProduct" class="btn btn-success">Save</button>
          <button @click="toggleAddForm" class="btn btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Spinner de Carregamento -->
    <div v-if="loading" class="spinner"></div>

    <!-- Grid de Cards de Produtos -->
    <div v-else class="card-container">
      <div v-for="product in filteredProducts" :key="product.id" class="card">
        <img
          :src="product.img"
          @error="handleImageError"
          alt="Product Image"
          class="card-img-top"
        />
        <div class="card-body">
          <h4 class="card-title">{{ product.name }}</h4>
          <p class="card-text">SKU: {{ product.sku }}</p>
          <button @click="toggleEdit(product)" class="btn btn-edit">
            Edit
          </button>
        </div>

        <!-- Edição Inline: overlay no card -->
        <div
          v-if="selectedProduct && selectedProduct.id === product.id"
          class="edit-overlay"
        >
          <h5>Edit Product</h5>
          <div class="form-group">
            <label>SKU</label>
            <input
              v-model="selectedProduct.sku"
              type="text"
              class="form-control"
            />
          </div>
          <div class="form-group">
            <label>Name</label>
            <input
              v-model="selectedProduct.name"
              type="text"
              class="form-control"
            />
          </div>
          <div class="form-group">
            <label>Image URL</label>
            <input
              v-model="selectedProduct.img"
              type="text"
              class="form-control"
            />
          </div>
          <div class="btn-group">
            <button @click="saveProduct" class="btn btn-success">Save</button>
            <button @click="cancelEdit" class="btn btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      allProducts: [],
      selectedProduct: null,
      searchQuery: '',
      showAddForm: false,
      loading: false,
      newProduct: {
        sku: '',
        name: '',
        img: '',
      },
    };
  },
  computed: {
    filteredProducts() {
      return this.allProducts.filter((product) => {
        const nameMatch = product.name
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase());
        const skuMatch = product.sku
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase());
        return nameMatch || skuMatch;
      });
    },
    ...mapGetters(['getProductsClient']),
  },
  methods: {
    async fetchProducts() {
      try {
        this.loading = true;
        await this.$store.dispatch('getClientProducts');
        this.allProducts = this.getProductsClient;
      } catch (error) {
        this.showErrorAlert('Error fetching products');
      } finally {
        this.loading = false;
      }
    },
    toggleAddForm() {
      this.showAddForm = !this.showAddForm;
    },
    async addNewProduct() {
      if (
        !this.newProduct.sku ||
        !this.newProduct.name ||
        !this.newProduct.img
      ) {
        return this.showErrorAlert('All fields are required!');
      }
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Do you want to add this product?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, add it!',
      });
      if (result.isConfirmed) {
        try {
          await this.$store.dispatch('createClientProduct', {
            sku: this.newProduct.sku,
            name: this.newProduct.name,
            img: this.newProduct.img,
          });
          this.showToast('Product added successfully');
          this.showAddForm = false;
          this.newProduct = { sku: '', name: '', img: '' };
          this.fetchProducts();
        } catch (error) {
          this.showErrorAlert('Failed to add product');
        }
      }
    },
    toggleEdit(product) {
      if (this.selectedProduct && this.selectedProduct.id === product.id) {
        this.selectedProduct = null;
      } else {
        this.selectedProduct = { ...product };
      }
    },
    async saveProduct() {
      if (!this.selectedProduct) return;
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Do you want to update this product?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, update it!',
      });
      if (result.isConfirmed) {
        try {
          await this.$store.dispatch('updateClientProduct', {
            id: this.selectedProduct.id,
            sku: this.selectedProduct.sku,
            name: this.selectedProduct.name,
            img: this.selectedProduct.img,
          });
          this.showToast('Product updated successfully');
          this.selectedProduct = null;
          this.fetchProducts();
        } catch (error) {
          this.showErrorAlert('Failed to update product');
        }
      }
    },
    cancelEdit() {
      this.selectedProduct = null;
    },
    handleImageError(e) {
      e.target.src = 'https://via.placeholder.com/150';
    },
    showToast(message) {
      Swal.fire({
        position: 'top-end',
        icon: 'success',
        title: message,
        showConfirmButton: false,
        timer: 1500,
      });
    },
    showErrorAlert(message) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: message,
      });
    },
  },
  async created() {
    await this.fetchProducts();
  },
};
</script>

<style scoped>
/* 
    Agora ocupamos a página toda, removendo max-width e 
    usando 100% de largura. 
  */
.page-wrapper {
  width: 100%;
  margin: 0;
  padding: 20px;
  box-sizing: border-box;
  font-family: 'Poppins', sans-serif;
}

/* Título */
h2 {
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
}

/* Barra de pesquisa */
.search-bar {
  text-align: center;
  margin-bottom: 10px;
}

.search-bar input[type='text'] {
  width: 40%;
  min-width: 200px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  text-align: center;
}

/* Botão de adicionar produtos (abaixo da barra de pesquisa) */
.add-button-container {
  text-align: center;
  margin-bottom: 20px;
}

.add-button-container button {
  padding: 8px 16px;
  background-color: #3498db;
  border: none;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
}

.add-button-container button:hover {
  background-color: #2980b9;
}

/* Card container grid */
.card-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

/* Card individual */
.card {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 10px;
  position: relative;
  padding: 10px;
  text-align: center;
  min-height: 320px; /* Para caber o overlay de edição */
}

/* Imagem do card: object-fit: contain para mostrar toda a imagem */
.card-img-top {
  width: 100%;
  height: 150px;
  object-fit: contain;
  object-position: center;
  background-color: #f9f9f9;
}

/* Card body */
.card-body {
  padding: 10px;
}

/* Título do card: tamanho menor */
.card-title {
  font-size: 14px;
  font-weight: bold;
  margin: 5px 0;
}

/* Texto do card */
.card-text {
  font-size: 12px;
  color: #555;
}

/* Botões padrão */
.btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-success {
  background-color: #2ecc71;
  color: #fff;
}

.btn-success:hover {
  background-color: #27ae60;
}

.btn-secondary {
  background-color: #95a5a6;
  color: #fff;
}

.btn-secondary:hover {
  background-color: #7f8c8d;
}

.btn-edit {
  background-color: #3498db;
  color: #fff;
  padding: 4px 8px;
  font-size: 12px;
  margin-top: 10px;
  border-radius: 4px;
}

.btn-edit:hover {
  background-color: #2980b9;
}

/* Overlay para edição inline */
.edit-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.edit-overlay .form-group {
  margin-bottom: 10px;
  width: 100%;
}

.edit-overlay input.form-control {
  text-align: center;
}

/* Form card (Add New Product) */
.form-card {
  max-width: 400px;
  margin: 0 auto 20px;
}

/* Spinner de carregamento */
.spinner {
  margin: 20px auto;
  border: 4px solid #ccc;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsividade: 1 coluna em telas muito pequenas */
@media (max-width: 767px) {
  .card-container {
    grid-template-columns: 1fr;
  }

  .search-bar input[type='text'] {
    width: 80%;
  }
}
</style>
